import { useEffect, useRef } from "react";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import type { TripCreateRecurringResponse } from "@mtform/shared";
import { Button } from "@/components/ui/button";
import { formatRecurringDate } from "@/lib/recurrence";

interface RecurringSuccessScreenProps {
  result: TripCreateRecurringResponse;
  /** The result includes a re-book, whose dates come in an email of their own. */
  rebooked?: boolean;
  /** Dates that failed and the user asked to retry. */
  onRetryFailed: (dates: string[]) => void;
  onCreateAnother: () => void;
}

export function RecurringSuccessScreen({
  result,
  rebooked = false,
  onRetryFailed,
  onCreateAnother,
}: RecurringSuccessScreenProps) {
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const { createdCount, failedCount, results } = result;
  const allOk = failedCount === 0;
  // Only dates dispatch clearly refused are re-booked from here. One it never
  // confirmed may already be booked, so it's checked in Booked Trips first.
  const failedDates = results
    .filter((r) => r.status === "failed" && !r.unconfirmed)
    .map((r) => r.date);
  const unconfirmed = results.filter(
    (r) => r.status === "failed" && r.unconfirmed,
  ).length;
  // Dates never sent because the booking ran out of time: dispatch didn't
  // refuse them, so they're not counted as refused in what the screen says.
  const notSent = results.filter(
    (r) => r.status === "failed" && r.notSent && !r.unconfirmed,
  ).length;
  const refused = failedDates.length - notSent;

  return (
    <section
      role="status"
      aria-live="polite"
      className="mx-auto flex max-w-xl flex-col items-center gap-5 px-4 py-10 text-center"
    >
      {allOk ? (
        <CheckCircle2
          className="h-20 w-20 text-success"
          strokeWidth={1.5}
          aria-hidden="true"
        />
      ) : (
        <AlertTriangle
          className="h-20 w-20 text-amber-500"
          strokeWidth={1.5}
          aria-hidden="true"
        />
      )}

      <h1
        ref={headingRef}
        tabIndex={-1}
        className="text-2xl font-bold leading-snug text-foreground focus:outline-none"
      >
        {allOk
          ? `All ${createdCount} trip${createdCount === 1 ? "" : "s"} booked`
          : `${createdCount} of ${createdCount + failedCount} trip${createdCount + failedCount === 1 ? "" : "s"} booked`}
      </h1>

      <p className="max-w-md text-sm text-muted-foreground">
        {allOk && rebooked
          ? "Every date is booked now. The re-booked dates come in a confirmation email of their own. Each date is an independent trip you can manage separately."
          : allOk
          ? "We'll review the bookings and send a single confirmation email with every trip's details. Each date is an independent trip you can manage separately."
          : unconfirmed === 0 && notSent === 0 && createdCount > 0
            ? "Most trips booked, but a few dates didn't go through. You can re-book just those below — the successful ones are already in dispatch's queue."
            : [
                unconfirmed === 1
                  ? "Dispatch didn't confirm the date marked “Not confirmed”: it may already be booked, so check Booked Trips before booking it again."
                  : unconfirmed > 1
                    ? "Dispatch didn't confirm the dates marked “Not confirmed”: they may already be booked, so check Booked Trips before booking them again."
                    : "",
                refused === 0
                  ? ""
                  : notSent > 0
                    ? `Dispatch couldn't accept ${refused === 1 ? "one date, so you can re-book it" : `${refused} dates, so you can re-book them`} below.`
                    : unconfirmed === 0
                      ? `Dispatch couldn't accept ${refused === 1 ? "this date. You can re-book it" : "these dates. You can re-book them"} below.`
                      : `Dispatch couldn't accept the ${refused === 1 ? "other one, so you can re-book it" : "others, so you can re-book them"} below.`,
                notSent === 0
                  ? ""
                  : `Booking took longer than usual, so ${notSent === 1 ? "one date wasn't sent. You can book it" : `${notSent} dates weren't sent. You can book them`} below.`,
                createdCount === 1
                  ? "The booked one is already in dispatch's queue."
                  : createdCount > 1
                    ? "The booked ones are already in dispatch's queue."
                    : "",
              ]
                .filter(Boolean)
                .join(" ")}
      </p>

      {/* Booked dates */}
      <div className="w-full rounded-2xl border border-border bg-foreground/[0.02] p-4 text-left">
        <ul className="grid gap-1.5">
          {results.map((r) => (
            <li
              key={r.date}
              className="flex items-center justify-between gap-3 border-b border-foreground/[0.06] pb-1.5 text-sm last:border-0 last:pb-0"
            >
              <span className="flex items-center gap-2">
                {r.status === "created" ? (
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
                ) : (
                  <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
                )}
                <span className="font-semibold text-foreground">
                  {formatRecurringDate(r.date)}
                </span>
              </span>
              <span className="text-right text-xs">
                {r.status === "created" ? (
                  <span className="font-mono text-muted-foreground">
                    {r.friendlyIds.length > 0
                      ? r.friendlyIds.join(" · ")
                      : "ID pending"}
                  </span>
                ) : (
                  <span className="font-semibold text-amber-600 dark:text-amber-400">
                    {r.error ?? "Failed"}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-3">
        {failedDates.length > 0 ? (
          <Button onClick={() => onRetryFailed(failedDates)}>
            Re-book {failedDates.length} failed date
            {failedDates.length === 1 ? "" : "s"}
          </Button>
        ) : null}
        <Button
          variant={failedDates.length > 0 ? "secondary" : "primary"}
          onClick={onCreateAnother}
        >
          Create another trip
        </Button>
      </div>
    </section>
  );
}
